import { getSettings } from './settings';
import { splitDataUrl } from './imageUtils';
import type { Settings } from '../types';

// Vision extraction of a debt slip, supporting two providers:
//  - Anthropic Messages API (/v1/messages, tool_use)
//  - OpenAI Chat Completions (/v1/chat/completions, function calling)
// Direct browser call (POC only). Prefer a proxy (Base URL) to hide the key + fix CORS.

export interface ExtractionResult {
  supplierName?: string;
  taxCode?: string;
  slipNumber?: string;
  slipDate?: string; // yyyy-mm-dd
  currentAmount?: number;
  oldDebt?: number;
  totalPayable?: number;
  bankInfoRaw?: string;
  rawText?: string;
  confidence?: number;
}

const SYSTEM = [
  'Bạn là trợ lý bóc tách "đơn nợ" của doanh nghiệp Việt Nam từ ảnh chụp.',
  'Chứng từ có thể là: phiếu xuất kho, hóa đơn bán hàng, phiếu giao nhận và thanh toán.',
  'Ảnh có thể bị xoay, nhàu, lóa, có chữ viết tay. Hãy đọc cẩn thận.',
  'Số tiền là VND — bỏ mọi dấu chấm/phẩy ngăn cách khi trả về (vd 21.506.261 -> 21506261).',
  'Nếu phiếu có "Nợ cũ" và "Tổng cộng": totalPayable = Tổng cộng; currentAmount = tiền hàng đơn lần này (không gồm nợ cũ).',
  'Nếu không có nợ cũ: currentAmount = totalPayable, oldDebt = 0.',
].join(' ');

// Shared JSON schema, reused by both providers.
const SCHEMA_PROPERTIES = {
  supplierName: { type: 'string', description: 'Tên nhà cung cấp / công ty bán hàng' },
  taxCode: { type: 'string', description: 'Mã số thuế (MST) nếu có, chỉ chữ số' },
  slipNumber: { type: 'string', description: 'Số phiếu / số hóa đơn' },
  slipDate: { type: 'string', description: 'Ngày trên phiếu, định dạng yyyy-mm-dd' },
  currentAmount: { type: 'number', description: 'Tiền hàng đơn lần này (VND), không gồm nợ cũ' },
  oldDebt: { type: 'number', description: 'Nợ cũ nếu có (VND), nếu không có ghi 0' },
  totalPayable: { type: 'number', description: 'Tổng tiền phải trả cuối cùng trên phiếu (VND)' },
  bankInfoRaw: { type: 'string', description: 'Thông tin ngân hàng/số tài khoản trên phiếu nếu có' },
  rawText: { type: 'string', description: 'Tóm tắt ngắn nội dung đọc được' },
  confidence: { type: 'number', description: 'Độ tự tin tổng thể 0..1' },
} as const;
const REQUIRED = ['supplierName', 'totalPayable'];

export async function extractDebtSlip(imageDataUrl: string): Promise<ExtractionResult> {
  const s = await getSettings();
  if (!s.claudeApiKey) {
    throw new Error('Chưa nhập khóa API. Vào “Cài đặt” để nhập khóa.');
  }
  return s.provider === 'openai'
    ? extractWithOpenAI(s, imageDataUrl)
    : extractWithAnthropic(s, imageDataUrl);
}

// ---------- Anthropic ----------
async function extractWithAnthropic(s: Settings, imageDataUrl: string): Promise<ExtractionResult> {
  const { base64, mediaType } = splitDataUrl(imageDataUrl);
  const base = (s.baseUrl || 'https://api.anthropic.com').replace(/\/+$/, '');

  const body = {
    model: s.model || 'claude-sonnet-5',
    max_tokens: 1500,
    system: SYSTEM,
    tools: [
      {
        name: 'tra_ket_qua',
        description: 'Trả kết quả bóc tách đơn nợ dưới dạng có cấu trúc.',
        input_schema: { type: 'object', properties: SCHEMA_PROPERTIES, required: REQUIRED },
      },
    ],
    tool_choice: { type: 'tool', name: 'tra_ket_qua' },
    messages: [
      {
        role: 'user',
        content: [
          { type: 'image', source: { type: 'base64', media_type: mediaType, data: base64 } },
          { type: 'text', text: 'Bóc tách phiếu này theo schema công cụ tra_ket_qua.' },
        ],
      },
    ],
  };

  const headers: Record<string, string> = {
    'content-type': 'application/json',
    'anthropic-version': '2023-06-01',
    'anthropic-dangerous-direct-browser-access': 'true',
  };
  if (s.authScheme === 'bearer') headers['authorization'] = `Bearer ${s.claudeApiKey}`;
  else headers['x-api-key'] = s.claudeApiKey;

  const res = await fetch(`${base}/v1/messages`, { method: 'POST', headers, body: JSON.stringify(body) });
  if (!res.ok) {
    throw new Error(`API Anthropic trả về lỗi ${res.status}. Vui lòng kiểm tra cấu hình và thử lại.`);
  }
  const data = await res.json();
  const toolUse = (data.content || []).find((c: any) => c.type === 'tool_use');
  if (!toolUse) throw new Error('AI không trả về kết quả bóc tách có cấu trúc.');
  return toolUse.input as ExtractionResult;
}

// ---------- OpenAI ----------
async function extractWithOpenAI(s: Settings, imageDataUrl: string): Promise<ExtractionResult> {
  const base = (s.baseUrl || 'https://api.openai.com').replace(/\/+$/, '');

  const body = {
    model: s.model || 'gpt-4o-mini',
    messages: [
      { role: 'system', content: SYSTEM },
      {
        role: 'user',
        content: [
          { type: 'text', text: 'Bóc tách phiếu này, gọi hàm tra_ket_qua với dữ liệu đọc được.' },
          { type: 'image_url', image_url: { url: imageDataUrl } },
        ],
      },
    ],
    tools: [
      {
        type: 'function',
        function: {
          name: 'tra_ket_qua',
          description: 'Trả kết quả bóc tách đơn nợ dưới dạng có cấu trúc.',
          parameters: { type: 'object', properties: SCHEMA_PROPERTIES, required: REQUIRED },
        },
      },
    ],
    tool_choice: { type: 'function', function: { name: 'tra_ket_qua' } },
  };

  const res = await fetch(`${base}/v1/chat/completions`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${s.claudeApiKey}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`API OpenAI trả về lỗi ${res.status}. Vui lòng kiểm tra cấu hình và thử lại.`);
  }
  const data = await res.json();
  const msg = data.choices?.[0]?.message;
  const argStr = msg?.tool_calls?.[0]?.function?.arguments ?? msg?.content;
  if (!argStr) throw new Error('AI (OpenAI) không trả về kết quả bóc tách.');
  try {
    return JSON.parse(argStr) as ExtractionResult;
  } catch {
    throw new Error('Không đọc được dữ liệu JSON do OpenAI trả về.');
  }
}

export interface BankCandidate {
  bin: string;
  name: string;
}

export interface BankMappingResult {
  sourceBankName: string;
  matchedBin: string;
  matchedBankName: string;
  confidence: number;
  reason?: string;
}

const BANK_MAPPING_PROPERTIES = {
  sourceBankName: { type: 'string' },
  matchedBin: { type: 'string' },
  matchedBankName: { type: 'string' },
  confidence: { type: 'number' },
  reason: { type: 'string' },
} as const;

export async function resolveBankWithAI(
  sourceBankName: string,
  candidates: BankCandidate[],
): Promise<BankMappingResult> {
  const s = await getSettings();
  if (!s.claudeApiKey) throw new Error('Chưa cấu hình khóa AI để ánh xạ ngân hàng.');
  const { sourceBankName: safeSource, candidates: safeCandidates } = sanitizeBankMappingInput(sourceBankName, candidates);
  if (!safeSource || !safeCandidates.length) throw new Error('Thiếu dữ liệu ánh xạ ngân hàng.');
  return s.provider === 'openai'
    ? resolveBankWithOpenAI(s, safeSource, safeCandidates)
    : resolveBankWithAnthropic(s, safeSource, safeCandidates);
}

export function sanitizeBankMappingInput(sourceBankName: string, candidates: BankCandidate[]) {
  const safeSource = sourceBankName
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\b(authorization|cookie|x-misa-context|bearer)\b\s*[:=]?\s*\S*/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 180);
  const safeCandidates = candidates.map(({ bin, name }) => ({
    bin: String(bin).replace(/\D/g, '').slice(0, 12),
    name: String(name).replace(/[\r\n\t]+/g, ' ').trim().slice(0, 100),
  }));
  return { sourceBankName: safeSource, candidates: safeCandidates };
}

async function resolveBankWithAnthropic(
  s: Settings,
  sourceBankName: string,
  candidates: BankCandidate[],
): Promise<BankMappingResult> {
  const base = (s.baseUrl || 'https://api.anthropic.com').replace(/\/+$/, '');
  const body = {
    model: s.model || 'claude-sonnet-5',
    max_tokens: 400,
    system: 'Chỉ chọn ngân hàng trong danh sách ứng viên. Không tự tạo BIN hoặc tên ngân hàng mới.',
    tools: [{
      name: 'tra_anh_xa_ngan_hang',
      description: 'Chọn ngân hàng ứng viên tương ứng với tên ngân hàng nguồn.',
      input_schema: {
        type: 'object',
        properties: BANK_MAPPING_PROPERTIES,
        required: ['sourceBankName', 'matchedBin', 'matchedBankName', 'confidence'],
      },
    }],
    tool_choice: { type: 'tool', name: 'tra_anh_xa_ngan_hang' },
    messages: [{
      role: 'user',
      content: `Tên nguồn: ${sourceBankName}\nỨng viên: ${JSON.stringify(candidates)}`,
    }],
  };
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    'anthropic-version': '2023-06-01',
    'anthropic-dangerous-direct-browser-access': 'true',
  };
  if (s.authScheme === 'bearer') headers.authorization = `Bearer ${s.claudeApiKey}`;
  else headers['x-api-key'] = s.claudeApiKey;
  const res = await fetch(`${base}/v1/messages`, { method: 'POST', headers, body: JSON.stringify(body) });
  if (!res.ok) throw new Error(`API Anthropic trả về lỗi ${res.status} khi ánh xạ ngân hàng.`);
  const data = await res.json();
  const toolUse = (data.content || []).find((c: any) => c.type === 'tool_use');
  if (!toolUse?.input) throw new Error('AI không trả về ánh xạ ngân hàng có cấu trúc.');
  return toolUse.input as BankMappingResult;
}

async function resolveBankWithOpenAI(
  s: Settings,
  sourceBankName: string,
  candidates: BankCandidate[],
): Promise<BankMappingResult> {
  const base = (s.baseUrl || 'https://api.openai.com').replace(/\/+$/, '');
  const body = {
    model: s.model || 'gpt-4o-mini',
    messages: [
      { role: 'system', content: 'Chỉ chọn ngân hàng trong danh sách ứng viên. Không tự tạo BIN hoặc tên mới.' },
      { role: 'user', content: `Tên nguồn: ${sourceBankName}\nỨng viên: ${JSON.stringify(candidates)}` },
    ],
    tools: [{
      type: 'function',
      function: {
        name: 'tra_anh_xa_ngan_hang',
        description: 'Chọn ngân hàng ứng viên tương ứng với tên ngân hàng nguồn.',
        parameters: {
          type: 'object',
          properties: BANK_MAPPING_PROPERTIES,
          required: ['sourceBankName', 'matchedBin', 'matchedBankName', 'confidence'],
        },
      },
    }],
    tool_choice: { type: 'function', function: { name: 'tra_anh_xa_ngan_hang' } },
  };
  const res = await fetch(`${base}/v1/chat/completions`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${s.claudeApiKey}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API OpenAI trả về lỗi ${res.status} khi ánh xạ ngân hàng.`);
  const data = await res.json();
  const args = data.choices?.[0]?.message?.tool_calls?.[0]?.function?.arguments;
  if (!args) throw new Error('AI không trả về ánh xạ ngân hàng có cấu trúc.');
  try {
    return JSON.parse(args) as BankMappingResult;
  } catch {
    throw new Error('Không đọc được JSON ánh xạ ngân hàng do AI trả về.');
  }
}
